import * as React from 'react';
import { SiteRoute } from '@journeyapps/package-types';
import { useInView, useReducedMotion } from 'motion/react';
import { useFontsLoaded, useImagesLoaded } from '../../../hooks';
import { STORY_SCENES, STORY_TIMELINE, StoryScene } from './story';
import { getStoryCaption } from './timeline';
import { platformStoryBanner, S } from './styles';
import { CUSTOMER_LOGOS, CustomerLogos } from '../CustomerLogos';
import { StoryWorld } from './artwork';
import { StoryWelcome } from './StoryWelcome';
import { StoryControls } from './StoryControls';

const INTRO_DURATION_MS = STORY_TIMELINE.introScreenMs;
const INTRO_SCROLL_CANCEL_THRESHOLD = 0.3;
const WALKTHROUGH_IMAGES = [platformStoryBanner, ...CUSTOMER_LOGOS.map((customer) => customer.logo)];

export const PlatformStoryWidget: React.FC<{ headingLevel?: 'h2' | 'h3'; withIntro?: boolean }> = ({
  headingLevel = 'h2',
  withIntro = false
}) => {
  const Heading = headingLevel;
  const reducedMotion = useReducedMotion();
  const stageRef = React.useRef<HTMLDivElement>(null);
  const inView = useInView(stageRef, { amount: 'some' });
  const [showingIntro, setShowingIntro] = React.useState(withIntro);
  const [scene, setScene] = React.useState<StoryScene>(StoryScene.APP);
  const [manuallySelected, setManuallySelected] = React.useState(false);
  const [playing, setPlaying] = React.useState(true);
  const [elapsed, setElapsed] = React.useState(0);
  const [visible, setVisible] = React.useState(true);
  const imagesLoaded = useImagesLoaded(WALKTHROUGH_IMAGES);
  const fontsLoaded = useFontsLoaded();
  const active = STORY_SCENES[scene];
  const animateConnections = inView && visible && !reducedMotion;
  const running = playing && animateConnections && imagesLoaded && fontsLoaded;

  React.useEffect(() => {
    const update = () => setVisible(document.visibilityState === 'visible');
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  React.useEffect(() => {
    if (!running) return;
    let previous = performance.now();
    let frame: number;
    const tick = (now: number) => {
      const delta = now - previous;
      previous = now;
      setElapsed((value) => value + delta);
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [running]);
  React.useEffect(() => {
    if (!playing) return;
    if (showingIntro) {
      if (elapsed >= INTRO_DURATION_MS) {
        setShowingIntro(false);
        setElapsed(0);
      }
      return;
    }
    if (elapsed < active.durationMs) return;
    if (scene === StoryScene.KEEP_BUILDING) setPlaying(false);
    else {
      setScene((value) => value + 1);
      setElapsed(0);
    }
  }, [elapsed, active.durationMs, scene, showingIntro, playing]);

  const startStory = () => {
    setManuallySelected(false);
    setShowingIntro(false);
    setScene(StoryScene.APP);
    setElapsed(0);
    setPlaying(true);
  };
  React.useEffect(() => {
    if (!withIntro || !showingIntro || !playing) return;
    const cancelIntroCountdown = () => {
      const bounds = stageRef.current?.getBoundingClientRect();
      if (bounds && bounds.top <= -bounds.height * INTRO_SCROLL_CANCEL_THRESHOLD) {
        setPlaying(false);
        setElapsed(0);
      }
    };
    cancelIntroCountdown();
    document.addEventListener('scroll', cancelIntroCountdown, { capture: true, passive: true });
    window.addEventListener('resize', cancelIntroCountdown);
    return () => {
      document.removeEventListener('scroll', cancelIntroCountdown, true);
      window.removeEventListener('resize', cancelIntroCountdown);
    };
  }, [withIntro, showingIntro, playing]);
  const goTo = (next: StoryScene) => {
    setManuallySelected(true);
    setShowingIntro(false);
    setPlaying(false);
    setScene(next);
    setElapsed(STORY_SCENES[next].durationMs);
  };
  const replay = () => {
    setManuallySelected(false);
    setShowingIntro(withIntro);
    setScene(StoryScene.APP);
    setElapsed(0);
    setPlaying(true);
  };
  const togglePlayback = () => {
    if (manuallySelected) {
      if (showingIntro) startStory();
      else if (scene === StoryScene.KEEP_BUILDING) replay();
      else {
        setManuallySelected(false);
        setScene((value) => value + 1);
        setElapsed(0);
        setPlaying(true);
      }
    } else if (scene === StoryScene.KEEP_BUILDING && elapsed >= active.durationMs) replay();
    else setPlaying((value) => !value);
  };
  const finale = scene === StoryScene.KEEP_BUILDING;
  const messageProgress = reducedMotion
    ? 1
    : Math.max(
        0,
        Math.min(1, (elapsed - STORY_TIMELINE.finale.messageStartMs) / STORY_TIMELINE.finale.messageDurationMs)
      );
  const caption = getStoryCaption(scene, elapsed, Boolean(reducedMotion));

  return (
    <S.Theater data-intro={showingIntro} data-finale={finale && !showingIntro} data-landing={withIntro}>
      <S.SceneViewport ref={stageRef}>
        <StoryWelcome visible={showingIntro} reducedMotion={Boolean(reducedMotion)} onStart={startStory} />
        {finale && (
          <S.FinaleMessage>
            <S.Eyebrow>KEEP BUILDING</S.Eyebrow>
            <Heading>And that’s just the beginning.</Heading>
            <p>Build on the same powerful foundation—with more capabilities for whatever comes next.</p>
          </S.FinaleMessage>
        )}
        <S.Caption
          style={{ visibility: showingIntro || finale ? 'hidden' : 'visible' }}
          aria-live={playing ? 'off' : 'polite'}
        >
          <div>
            <Heading>{active.heading}</Heading>
            <p>{caption}</p>
          </div>
        </S.Caption>
        <S.Stage style={{ opacity: showingIntro ? 0.25 : 1 }}>
          <StoryWorld
            scene={scene}
            elapsed={elapsed}
            showingIntro={showingIntro}
            reducedMotion={Boolean(reducedMotion)}
            animateConnections={animateConnections}
          />
          {finale && (
            <S.FinaleAction
              style={{ opacity: messageProgress, visibility: messageProgress > 0 ? 'visible' : 'hidden' }}
            >
              <S.ExploreLink to={SiteRoute.FEATURES}>
                Explore the platform <span aria-hidden="true">→</span>
              </S.ExploreLink>
            </S.FinaleAction>
          )}
        </S.Stage>
      </S.SceneViewport>
      <StoryControls
        scene={scene}
        elapsed={elapsed}
        showingIntro={showingIntro}
        withIntro={withIntro}
        playing={playing}
        manuallySelected={manuallySelected}
        reducedMotion={Boolean(reducedMotion)}
        onSelectScene={goTo}
        onSelectIntro={() => {
          replay();
          setPlaying(false);
          setManuallySelected(true);
          setElapsed(INTRO_DURATION_MS);
        }}
        onTogglePlayback={togglePlayback}
        onReplay={replay}
      />
    </S.Theater>
  );
};
